import React, { useState } from "react";
import {
  Layout,
  Input,
  Avatar,
  Badge,
  Button,
  Dropdown,
  MenuProps,
} from "antd";
import { useGetIdentity, useLogout } from "@refinedev/core";
import {
  Search,
  Globe,
  Bell,
  Settings,
  User,
  LogOut,
  ChevronDown,
} from "lucide-react";
import "./Header.css";

const { Header: AntHeader } = Layout;
const { Search: AntSearch } = Input;

interface HeaderProps {
  title?: string;
  subtitle?: string;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  showSearch?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  searchPlaceholder = "Rechercher...",
  onSearch,
  showSearch = true,
}) => {
  const { data: identity } = useGetIdentity();
  const { mutate: logout } = useLogout();
  const [searchValue, setSearchValue] = useState("");

  const handleSearch = (value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  };

  const handleLogout = () => {
    logout();
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <User size={16} />,
      label: "Mon Profil",
    },
    {
      key: "settings",
      icon: <Settings size={16} />,
      label: "Paramètres",
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogOut size={16} />,
      label: "Déconnexion",
      danger: true,
      onClick: handleLogout,
    },
  ];

  const languageMenuItems: MenuProps["items"] = [
    {
      key: "fr",
      label: "Français",
    },
    {
      key: "en",
      label: "English",
    },
    {
      key: "es",
      label: "Español",
    },
  ];

  return (
    <AntHeader className="header">
      <div className="header-left">
        {title && (
          <div className="page-title-section">
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
        )}
      </div>

      <div className="header-center">
        {showSearch && (
          <AntSearch
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            className="header-search"
            prefix={<Search size={16} />}
            allowClear
          />
        )}
      </div>

      <div className="header-right">
        <div className="header-actions">
          <Dropdown
            menu={{ items: languageMenuItems }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button
              type="text"
              icon={<Globe size={18} />}
              className="header-action-button"
            />
          </Dropdown>

          <Badge count={3} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<Bell size={18} />}
              className="header-action-button"
            />
          </Badge>

          <Button
            type="text"
            icon={<Settings size={18} />}
            className="header-action-button"
          />

          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <div className="user-profile-dropdown">
              <Avatar
                size={32}
                src={(identity as any)?.profileImageUrl}
                className="user-avatar"
              >
                {(identity as any)?.username?.charAt(0).toUpperCase()}
              </Avatar>
              <ChevronDown size={14} className="dropdown-arrow" />
            </div>
          </Dropdown>
        </div>
      </div>
    </AntHeader>
  );
};
